import { isMobileDevice } from '../utils/device.js';
import { VirtualJoystick } from './VirtualJoystick.js';

/**
 * UI Manager - Handles all game UI elements and transitions
 */

export class UIManager {
    constructor() {
        this.elements = {
            loading: document.getElementById('loading'),
            menu: document.getElementById('menu'),
            hud: document.getElementById('hud'),
            gameStatus: document.getElementById('gameStatus'),
            gameOver: null // Will be created dynamically
        };

        this.hudElements = {
            health: document.getElementById('health'),
            gold: document.getElementById('gold'),
            level: document.getElementById('level'),
            score: document.getElementById('score')
        };

        this.gameStatusElements = {
            kills: document.getElementById('killCount'),
            gold: document.getElementById('goldCount'),
            enemies: document.getElementById('enemyCount')
        };

        this.currentScreen = 'loading';

        this.isMobileUI = isMobileDevice();
        this.joystick = this.isMobileUI ? new VirtualJoystick({ parent: document.getElementById('gameContainer') }) : null;
        if (this.joystick) {
            this.joystick.init();
            this.joystick.hide();
        }

        // Loading progress elements (optional)
        this.loadingBarInner = document.getElementById('loadingBarInner');
        this.loadingText = document.getElementById('loadingText');
        this.loadingProgressState = {
            lastPercent: 0,
            finalized: false
        };

        this.rescueOverlay = null;
        this.rescueLabel = null;
        this.rescueBarOuter = null;
        this.rescueBarInner = null;
        this.rescueActionButton = null;
        this.rescueClickHandler = null;

        this.fpsOverlay = null;
        this.fpsValueElement = null;
        this.frameTimeElement = null;
        this.fpsEma = null;
        this.lastFpsDisplay = '';
        this.lastFrameTimeDisplay = '';
        this.lastFpsUpdate = 0;
        this.fpsSmoothingFactor = 0.2;
    }

    ensureRescueOverlay() {
        if (this.rescueOverlay) {
            return;
        }

        const container = document.createElement('div');
        container.id = 'rescueOverlay';
        container.style.cssText = `
            position: absolute;
            bottom: ${this.isMobileUI ? '36px' : '100px'};
            left: 50%;
            transform: translateX(-50%);
            background: rgba(0, 40, 70, 0.85);
            padding: ${this.isMobileUI ? '10px 16px' : '12px 20px'};
            border-radius: 12px;
            border: 1px solid rgba(0, 170, 255, 0.6);
            display: none;
            z-index: 160;
            width: ${this.isMobileUI ? 'calc(100vw - 32px)' : 'auto'};
            max-width: ${this.isMobileUI ? '320px' : '360px'};
            min-width: ${this.isMobileUI ? '0' : '240px'};
            color: #dff6ff;
            font-family: 'Arial', sans-serif;
            box-shadow: 0 8px 18px rgba(0, 0, 0, 0.35);
        `;

        const label = document.createElement('div');
        label.style.cssText = `
            text-align: center;
            margin-bottom: ${this.isMobileUI ? '6px' : '8px'};
            font-size: ${this.isMobileUI ? '14px' : '15px'};
            letter-spacing: 0.5px;
        `;
        label.textContent = '救援进行中';

        const barOuter = document.createElement('div');
        barOuter.style.cssText = `
            width: 100%;
            height: ${this.isMobileUI ? '8px' : '10px'};
            background: rgba(255, 255, 255, 0.12);
            border-radius: 6px;
            overflow: hidden;
        `;

        const barInner = document.createElement('div');
        barInner.style.cssText = `
            width: 0%;
            height: 100%;
            background: linear-gradient(90deg, #00aaff 0%, #2dd4bf 100%);
            transition: width 0.15s ease-out;
        `;

        barOuter.appendChild(barInner);
        container.appendChild(label);
        container.appendChild(barOuter);

        const actionButton = document.createElement('button');
        actionButton.type = 'button';
        const actionButtonStyles = [
            'margin-top: 10px;',
            'width: 100%;',
            `padding: ${this.isMobileUI ? '10px 0' : '12px 0'};`,
            'border: none;',
            'border-radius: 6px;',
            'background: linear-gradient(90deg, #2dd4bf 0%, #00aaff 100%);',
            'color: #03203b;',
            'font-weight: 700;',
            `font-size: ${this.isMobileUI ? '13px' : '14px'};`,
            'cursor: pointer;',
            'display: none;'
        ];

        if (this.isMobileUI) {
            actionButtonStyles.push('min-height: 40px;');
        }

        actionButton.style.cssText = actionButtonStyles.join('\n');
        actionButton.textContent = this.isMobileUI ? '点击救援' : '右键救援';

        let lastRescueTrigger = 0;

        const triggerRescue = (event) => {
            const now = Date.now();
            if (now - lastRescueTrigger < 100) {
                event?.preventDefault?.();
                event?.stopPropagation?.();
                return;
            }
            lastRescueTrigger = now;
            if (event) {
                event.preventDefault?.();
                event.stopPropagation?.();
            }
            if (typeof this.rescueClickHandler === 'function') {
                this.rescueClickHandler();
            }
        };

        actionButton.addEventListener('click', (event) => {
            if (this.isMobileUI) {
                triggerRescue(event);
            } else {
                event.preventDefault();
                event.stopPropagation();
            }
        });

        actionButton.addEventListener('pointerup', (event) => {
            if (!this.isMobileUI && event && event.button === 2) {
                triggerRescue(event);
            } else if (!this.isMobileUI) {
                event.preventDefault();
                event.stopPropagation();
            }
        });

        actionButton.addEventListener('contextmenu', (event) => {
            event.preventDefault();
            event.stopPropagation();
            if (!this.isMobileUI) {
                triggerRescue(event);
            }
        });

        container.appendChild(actionButton);

        const parent = document.getElementById('gameContainer') || document.body;
        parent.appendChild(container);

        this.rescueOverlay = container;
        this.rescueLabel = label;
        this.rescueBarOuter = barOuter;
        this.rescueBarInner = barInner;
        this.rescueActionButton = actionButton;
    }

    showRescueProgress(progress = 0, text = '') {
        this.ensureRescueOverlay();
        if (!this.rescueOverlay) return;

        if (this.rescueLabel) {
            this.rescueLabel.textContent = text || '救援进行中';
        }

        if (this.rescueBarOuter) {
            this.rescueBarOuter.style.display = 'block';
        }
        if (this.rescueBarInner) {
            const pct = Math.max(0, Math.min(1, progress)) * 100;
            this.rescueBarInner.style.width = `${pct.toFixed(1)}%`;
        }
        if (this.rescueActionButton) {
            this.rescueActionButton.style.display = 'none';
        }

        this.rescueOverlay.style.display = 'block';
    }

    showRescueMessage(text = '') {
        this.ensureRescueOverlay();
        if (!this.rescueOverlay) return;

        if (this.rescueLabel) {
            this.rescueLabel.textContent = text || '无法救援';
        }
        if (this.rescueBarOuter) {
            this.rescueBarOuter.style.display = 'none';
        }
        if (this.rescueActionButton) {
            this.rescueActionButton.style.display = 'none';
        }
        this.rescueOverlay.style.display = 'block';
    }

    showRescuePrompt(text = '发现待救居民', buttonText = '开始救援', disabled = false) {
        this.ensureRescueOverlay();
        if (!this.rescueOverlay) return;

        if (this.rescueLabel) {
            this.rescueLabel.textContent = text;
        }
        if (this.rescueBarOuter) {
            this.rescueBarOuter.style.display = 'none';
        }
        if (this.rescueBarInner) {
            this.rescueBarInner.style.width = '0%';
        }
        if (this.rescueActionButton) {
            this.rescueActionButton.textContent = buttonText;
            this.rescueActionButton.disabled = Boolean(disabled);
            this.rescueActionButton.style.display = 'block';
        }

        this.rescueOverlay.style.display = 'block';
    }

    setRescueAction(handler) {
        this.rescueClickHandler = handler;
    }

    hideRescueStatus() {
        if (!this.rescueOverlay) return;
        this.rescueOverlay.style.display = 'none';
        if (this.rescueActionButton) {
            this.rescueActionButton.style.display = 'none';
        }
    }

    ensureFPSOverlay() {
        // Temporarily hide FPS overlay
        return;

        if (this.fpsOverlay || typeof document === 'undefined') {
            return;
        }

        if (!document.body) {
            if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
                window.requestAnimationFrame(() => this.ensureFPSOverlay());
            } else {
                setTimeout(() => this.ensureFPSOverlay(), 16);
            }
            return;
        }

        const container = document.createElement('div');
        container.id = 'fpsOverlay';
        container.innerHTML = `
            <div class="fps-metric">
                <span class="label">FPS</span>
                <span class="value">--</span>
            </div>
            <div class="fps-metric">
                <span class="label">Frame</span>
                <span class="value" data-kind="ms">--</span>
                <span class="suffix">ms</span>
            </div>
        `;
        document.body.appendChild(container);
        const valueEls = container.querySelectorAll('.value');
        this.fpsOverlay = container;
        this.fpsValueElement = valueEls[0] || null;
        this.frameTimeElement = valueEls[1] || null;
    }

    updateFPS(rawFps) {
        if (!Number.isFinite(rawFps) || rawFps < 0) {
            return;
        }
        this.ensureFPSOverlay();
        if (!this.fpsOverlay || !this.fpsValueElement || !this.frameTimeElement) {
            return;
        }
        const now = (typeof performance !== 'undefined' && performance.now) ? performance.now() : Date.now();
        if (this.lastFpsUpdate && now - this.lastFpsUpdate < 80) {
            return;
        }
        if (this.fpsEma === null) {
            this.fpsEma = rawFps;
        } else {
            this.fpsEma += (rawFps - this.fpsEma) * this.fpsSmoothingFactor;
        }
        const fps = Math.max(0, this.fpsEma);
        const frameMs = fps > 0 ? 1000 / fps : 0;
        const fpsText = fps >= 100 ? fps.toFixed(0) : fps.toFixed(1);
        const frameText = frameMs >= 10 ? frameMs.toFixed(1) : frameMs.toFixed(2);
        if (fpsText !== this.lastFpsDisplay) {
            this.fpsValueElement.textContent = fpsText;
            this.lastFpsDisplay = fpsText;
        }
        if (frameText !== this.lastFrameTimeDisplay) {
            this.frameTimeElement.textContent = frameText;
            this.lastFrameTimeDisplay = frameText;
        }
        this.updateFPSColor(fps);
        this.lastFpsUpdate = now;
    }

    updateFPSColor(fps) {
        if (!this.fpsOverlay || !this.fpsValueElement || !this.frameTimeElement) {
            return;
        }
        let color = '#2dd4bf';
        if (fps < 30) {
            color = '#f87171';
        } else if (fps < 50) {
            color = '#facc15';
        }
        this.fpsValueElement.style.color = color;
        this.frameTimeElement.style.color = color;
    }

    setJoystickHandler(handler) {
        if (this.joystick) {
            this.joystick.setMoveHandler(handler);
        }
    }

    showJoystick() {
        if (this.joystick) {
            this.joystick.show();
        }
    }

    hideJoystick() {
        if (this.joystick) {
            this.joystick.hide();
        }

        this.hideRescueStatus();
    }

    hasJoystick() {
        return !!this.joystick;
    }

    showLoading() {
        this.hideAll();
        if (this.loadingProgressState) {
            this.loadingProgressState.lastPercent = 0;
            this.loadingProgressState.finalized = false;
        }
        if (this.joystick) {
            this.joystick.hide();
        }
        this.elements.loading.style.display = 'block';
        this.currentScreen = 'loading';
    }

    hideLoading() {
        this.elements.loading.style.display = 'none';
    }

    updateLoadingProgress(percent = 0, options = {}) {
        if (!this.loadingProgressState) {
            this.loadingProgressState = {
                lastPercent: 0,
                finalized: false
            };
        }

        const finalize = options && options.finalize === true;
        let clamped = Math.round(Number.isFinite(percent) ? percent : 0);
        clamped = Math.max(0, Math.min(100, clamped));

        if (finalize) {
            this.loadingProgressState.finalized = true;
            clamped = 100;
        } else if (!this.loadingProgressState.finalized) {
            if (clamped >= 100) {
                clamped = 99;
            }
            if (clamped < this.loadingProgressState.lastPercent) {
                clamped = this.loadingProgressState.lastPercent;
            }
        } else {
            clamped = Math.max(this.loadingProgressState.lastPercent, clamped);
        }

        this.loadingProgressState.lastPercent = clamped;

        if (this.loadingBarInner) {
            this.loadingBarInner.style.width = `${clamped}%`;
        }
        if (this.loadingText) {
            this.loadingText.textContent = `Loading Pirate Clash... ${clamped}%`;
        }
    }

    showMenu() {
        this.hideAll();
        if (this.joystick) {
            this.joystick.hide();
        }
        this.elements.menu.style.display = 'block';
        this.currentScreen = 'menu';
    }

    hideMenu() {
        this.elements.menu.style.display = 'none';
    }

    showHUD() {
        // HUD临时关闭（左侧白色文字已不需要）
        this.hideAll();
        this.elements.hud.style.display = 'none';
        this.currentScreen = 'hud';
    }

    hideHUD() {
        this.elements.hud.style.display = 'none';
    }

    showGameStatus() {
        if (this.elements.gameStatus) {
            this.elements.gameStatus.style.display = 'flex';
        }
    }

    hideGameStatus() {
        if (this.elements.gameStatus) {
            this.elements.gameStatus.style.display = 'none';
        }
    }

    updateGameStatus(stats) {
        if (!this.elements.gameStatus) return;

        if (stats.kills !== undefined && this.gameStatusElements.kills) {
            this.gameStatusElements.kills.textContent = stats.kills;
        }

        if (stats.gold !== undefined && this.gameStatusElements.gold) {
            this.gameStatusElements.gold.textContent = stats.gold;
        }

        if (stats.enemies !== undefined && this.gameStatusElements.enemies) {
            this.gameStatusElements.enemies.textContent = stats.enemies;
        }
    }

    showGameOver(stats) {
        this.hideAll();

        if (!this.elements.gameOver) {
            this.createGameOverScreen();
        }

        this.updateGameOverStats(stats);
        this.elements.gameOver.style.display = 'block';
        this.currentScreen = 'gameOver';
        if (this.joystick) {
            this.joystick.hide();
        }
    }

    createGameOverScreen() {
        const gameOverHTML = `
            <div id="gameOver" style="
                position: absolute;
                top: 50%;
                left: 50%;
                transform: translate(-50%, -50%);
                text-align: center;
                z-index: 200;
                background: rgba(0, 20, 40, 0.95);
                padding: 40px;
                border-radius: 10px;
                border: 2px solid #ff4444;
                display: none;
            ">
                <h2 style="color: #ff4444; margin-bottom: 20px;">Game Over</h2>
                <div style="margin-bottom: 20px;">
                    <div>Final Score: <span id="finalScore">0</span></div>
                    <div>Ships Destroyed: <span id="shipsDestroyed">0</span></div>
                    <div>Survival Time: <span id="survivalTime">0:00</span></div>
                    <div>Gold Earned: <span id="goldEarned">0</span></div>
                </div>
                <button class="button" id="playAgain">Play Again</button>
                <button class="button" id="backToMenu">Main Menu</button>
            </div>
        `;

        document.getElementById('gameContainer').insertAdjacentHTML('beforeend', gameOverHTML);
        this.elements.gameOver = document.getElementById('gameOver');

        // Setup event listeners
        document.getElementById('playAgain').addEventListener('click', () => {
            this.restartGame();
        });

        document.getElementById('backToMenu').addEventListener('click', () => {
            this.returnToMenu();
        });
    }

    updateGameOverStats(stats) {
        if (this.elements.gameOver) {
            document.getElementById('finalScore').textContent = stats.score || 0;
            document.getElementById('shipsDestroyed').textContent = stats.shipsDestroyed || 0;
            document.getElementById('survivalTime').textContent = this.formatTime(stats.survivalTime || 0);
            document.getElementById('goldEarned').textContent = stats.goldEarned || 0;
        }
    }

    updateHUD(playerStats) {
        if (this.currentScreen === 'hud') {
            this.hudElements.health.textContent = Math.ceil(playerStats.health || 0);
            this.hudElements.gold.textContent = playerStats.gold || 0;
            this.hudElements.level.textContent = playerStats.level || 1;
            this.hudElements.score.textContent = playerStats.score || 0;
        }
    }

    showError(message) {
        const errorDiv = document.createElement('div');
        errorDiv.style.cssText = `
            position: fixed;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            background: rgba(255, 0, 0, 0.9);
            color: white;
            padding: 20px;
            border-radius: 10px;
            z-index: 1000;
            text-align: center;
        `;
        errorDiv.innerHTML = `
            <div>${message}</div>
            <button onclick="this.parentElement.remove()" style="margin-top: 10px; padding: 5px 15px;">OK</button>
        `;
        document.body.appendChild(errorDiv);
    }

    showNotification(message, type = 'info', duration = 3000) {
        const notification = document.createElement('div');
        const colors = {
            info: '#00aaff',
            success: '#00ff88',
            warning: '#ffaa00',
            error: '#ff4444'
        };

        notification.style.cssText = `
            position: fixed;
            top: 20px;
            right: 20px;
            background: ${colors[type] || colors.info};
            color: white;
            padding: 15px 20px;
            border-radius: 5px;
            z-index: 1000;
            animation: slideIn 0.3s ease-out;
        `;

        notification.textContent = message;
        document.body.appendChild(notification);

        setTimeout(() => {
            notification.style.animation = 'slideOut 0.3s ease-out';
            setTimeout(() => notification.remove(), 300);
        }, duration);
    }

    hideAll() {
        Object.values(this.elements).forEach(element => {
            if (element) {
                element.style.display = 'none';
            }
        });

        if (this.joystick) {
            this.joystick.hide();
        }
    }

    formatTime(seconds) {
        const minutes = Math.floor(seconds / 60);
        const remainingSeconds = Math.floor(seconds % 60);
        return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
    }

    restartGame() {
        // This should be handled by the main game app
        if (window.gameApp) {
            window.gameApp.startGame();
        }
    }

    returnToMenu() {
        // This should be handled by the main game app
        if (window.gameApp) {
            window.gameApp.returnToMenu();
        }
    }

    // Add CSS animations
    injectStyles() {
        const style = document.createElement('style');
        style.textContent = `
            @keyframes slideIn {
                from { transform: translateX(100%); opacity: 0; }
                to { transform: translateX(0); opacity: 1; }
            }

            @keyframes slideOut {
                from { transform: translateX(0); opacity: 1; }
                to { transform: translateX(100%); opacity: 0; }
            }

            .ui-transition {
                transition: all 0.3s ease-in-out;
            }

            .fade-in {
                animation: fadeIn 0.5s ease-in-out;
            }

            @keyframes fadeIn {
                from { opacity: 0; }
                to { opacity: 1; }
            }

            #fpsOverlay {
                position: fixed;
                top: 16px;
                left: 16px;
                padding: 10px 14px;
                background: rgba(6, 30, 53, 0.78);
                border-radius: 10px;
                border: 1px solid rgba(56, 189, 248, 0.4);
                color: #dbeafe;
                font-family: 'Arial', sans-serif;
                font-size: 13px;
                letter-spacing: 0.4px;
                z-index: 220;
                pointer-events: none;
                box-shadow: 0 12px 24px rgba(2, 12, 27, 0.35);
                backdrop-filter: blur(6px);
            }

            #fpsOverlay .fps-metric {
                display: flex;
                align-items: baseline;
                gap: 6px;
            }

            #fpsOverlay .fps-metric + .fps-metric {
                margin-top: 4px;
            }

            #fpsOverlay .label {
                text-transform: uppercase;
                font-weight: 600;
                color: rgba(191, 219, 254, 0.75);
                font-size: 11px;
                letter-spacing: 0.6px;
            }

            #fpsOverlay .value {
                font-weight: 700;
                font-size: 16px;
                min-width: 44px;
                text-align: right;
                color: #2dd4bf;
            }

            #fpsOverlay .suffix {
                font-size: 11px;
                color: rgba(191, 219, 254, 0.7);
            }
        `;
        document.head.appendChild(style);
    }
}

// Auto-inject styles when module loads
const uiManager = new UIManager();
uiManager.injectStyles();

